import type { JobCompletionPhotoPolicy } from '../hooks/useAppSettings'
import {
  fetchJobTasks,
  reconcilePhotoRequiredTasksDone,
  type JobTask,
} from '../api/jobWorkflow'
import { jobMeetsAllPhotoRequirements } from './jobPhotoPolicy'

/** Same rule as My Day: empty list or every task checked off. */
export function isJobChecklistComplete(tasks: Pick<JobTask, 'is_done'>[]): boolean {
  return tasks.length === 0 || tasks.every((t) => t.is_done)
}

export interface JobCompletionGateResult {
  ok: boolean
  message?: string
  incompleteChecklistLabels?: string[]
  missingTaskLabels?: string[]
  /** Which tab/section the UI should surface so the user can resolve the blocker. */
  redirectTo?: 'work' | 'gallery'
}

/**
 * Shared "can this job be marked complete?" gate used by JobDetails, DaySchedule,
 * and MyDay. Checks (in order): checklist completion, then photo policy
 * (global gallery requirement + per-task required photos).
 */
export async function evaluateJobCompletionGate(
  jobId: string,
  photoPolicy: JobCompletionPhotoPolicy
): Promise<JobCompletionGateResult> {
  // Heal optimistic auto-checks that never persisted (photo exists, is_done false).
  try {
    await reconcilePhotoRequiredTasksDone(jobId)
  } catch {
    /* gate still evaluates authoritative task rows below */
  }

  const tasks = await fetchJobTasks(jobId)
  const incomplete = tasks.filter((t) => !t.is_done)
  if (!isJobChecklistComplete(tasks)) {
    const labels = incomplete.map((t) => t.label)
    const preview = labels.slice(0, 3).join('; ')
    const more = labels.length > 3 ? ` (+${labels.length - 3} more)` : ''
    return {
      ok: false,
      incompleteChecklistLabels: labels,
      message: `Finish the checklist before completing this job: ${preview}${more}`,
      redirectTo: 'work',
    }
  }

  const photoCheck = await jobMeetsAllPhotoRequirements(jobId, photoPolicy)
  if (!photoCheck.ok) {
    return {
      ok: false,
      message: photoCheck.message,
      missingTaskLabels: photoCheck.missingTaskLabels,
      redirectTo: photoCheck.missingTaskLabels && photoCheck.missingTaskLabels.length > 0 ? 'work' : 'gallery',
    }
  }

  return { ok: true }
}
